import { Logo } from "./Header";
import { Link } from "react-router-dom";
import { ExternalLink, MessageCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";

// Inline icon components for X (Twitter), TikTok, YouTube, Facebook, LinkedIn — Lucide doesn't always ship the brand glyphs we need.
const IconX = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.969 6.817H1.676l7.73-8.835L1.254 2.25h6.83l4.713 6.231 5.447-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z"/>
  </svg>
);
const IconTikTok = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.74a8.16 8.16 0 0 0 4.77 1.52V6.81a4.85 4.85 0 0 1-1.84-.12Z"/>
  </svg>
);
const IconYouTube = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.016 3.016 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.016 3.016 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z"/>
  </svg>
);
const IconFacebook = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M24 12a12 12 0 1 0-13.875 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953h-1.513c-1.491 0-1.957.926-1.957 1.875V12h3.328l-.532 3.47h-2.796v8.384A12.001 12.001 0 0 0 24 12Z"/>
  </svg>
);
const IconLinkedIn = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.452 20.452h-3.554v-5.569c0-1.328-.026-3.038-1.852-3.038-1.853 0-2.136 1.447-2.136 2.94v5.667H9.356V9h3.41v1.561h.046c.476-.9 1.637-1.852 3.37-1.852 3.602 0 4.27 2.37 4.27 5.455v6.288ZM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124Zm1.778 13.019H3.557V9h3.558v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.226.792 24 1.771 24h20.451C23.2 24 24 23.226 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z"/>
  </svg>
);

export default function Footer() {
  const { t } = useI18n();
  const linkSocial = "h-9 w-9 rounded-full border hairline grid place-items-center text-muted hover:text-teal hover:border-teal/60 transition";
  const muted = "text-muted";

  return (
    <footer className="relative mt-32 border-t hairline">
      <div className="mx-auto max-w-7xl px-5 md:px-8 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo />
            <div className={`mt-6 max-w-md text-sm leading-relaxed ${muted}`}>
              <p>{t("footer.tagline_line_1", "Crypto-native travel, nightlife and lifestyle concierge.")}</p>
              <p className="mt-3">{t("footer.tagline_line_2", "Available now in Ibiza, Spain. Soon worldwide.")}</p>
            </div>

            <div className="mt-7">
              <div className="label-tiny mb-3">{t("footer.follow_us", "Follow Meet & Mint")}</div>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/meetandmint" target="_blank" rel="noreferrer" aria-label="Facebook" className={linkSocial} data-testid="social-facebook">
                  <IconFacebook size={14} />
                </a>
                <a href="https://t.me/meetandmint" target="_blank" rel="noreferrer" aria-label="Telegram" className={linkSocial} data-testid="social-telegram">
                  <MessageCircle size={14} />
                </a>
                <a href="https://x.com/meetandmint" target="_blank" rel="noreferrer" aria-label="X / Twitter" className={linkSocial} data-testid="social-x">
                  <IconX size={13} />
                </a>
                <a href="https://www.youtube.com/@MeetAndMint" target="_blank" rel="noreferrer" aria-label="YouTube" className={linkSocial} data-testid="social-youtube">
                  <IconYouTube size={14} />
                </a>
                <a href="https://www.tiktok.com/@meetandmint" target="_blank" rel="noreferrer" aria-label="TikTok" className={linkSocial} data-testid="social-tiktok">
                  <IconTikTok size={14} />
                </a>
                <a href="https://www.linkedin.com/company/meetandmint/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className={linkSocial} data-testid="social-linkedin">
                  <IconLinkedIn size={13} />
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="label-tiny mb-4">{t("footer.destinations_title", "Destinations")}</div>
            <ul className={`space-y-2 text-sm ${muted}`}>
              <li><Link to="/destinations/ibiza-spain" className="hover:text-teal">{t("header.nav_ibiza", "Ibiza")}</Link></li>
              <li><Link to="/destinations" className="hover:text-teal">{t("header.all_destinations", "All destinations")}</Link></li>
              <li><Link to="/custom-trip" className="hover:text-teal">{t("header.nav_custom_trip", "Custom trip")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="label-tiny mb-4">{t("footer.for_partners", "For partners")}</div>
            <ul className={`space-y-2 text-sm ${muted}`}>
              <li><Link to="/become-provider" className="hover:text-teal" data-testid="footer-become-provider">{t("footer.become_provider", "Become a service provider")}</Link></li>
              <li><Link to="/become-affiliate" className="hover:text-teal" data-testid="footer-become-affiliate">{t("footer.become_affiliate", "Become an affiliate")}</Link></li>
              <li><Link to="/login" className="hover:text-teal">{t("common.sign_in", "Sign in")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="label-tiny mb-4">{t("footer.brand_universe", "Brand universe")}</div>
            <p className={`text-sm leading-relaxed ${muted}`}>
              {t("footer.brand_universe_copy", "Meet & Mint Concierge is the lifestyle & travel arm of the Meet & Mint social wallet. Built for the new mobile, on-chain generation.")}
            </p>
            <a href="https://meetmint.app" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 mt-4 text-sm text-teal hover:underline"
               data-testid="footer-meetmint-app">
              {t("footer.networking_app", "Meet & Mint Networking App")}
              <ExternalLink size={12}/>
            </a>
          </div>
        </div>

        <div className="divider-x my-10" />
        <div className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs ${muted}`}>
          <div>© {new Date().getFullYear()} Meet &amp; Mint Concierge. {t("footer.rights_reserved", "All rights reserved.")}</div>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" className="hover:text-teal">{t("footer.privacy", "Privacy")}</Link>
            <Link to="/terms" className="hover:text-teal">{t("footer.terms", "Terms")}</Link>
            <button
              type="button"
              onClick={() => window.openCookieBanner?.()}
              className="hover:text-teal"
              data-testid="footer-cookie-prefs"
            >
              {t("footer.cookie_prefs", "Cookie preferences")}
            </button>
            <span>{t("footer.protected_deposits", "Protected deposits")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
